import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import PropTypes from "prop-types";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import SectionLayout from "reusables/layouts/SectionLayout";

const productRows = [
  {
    icon: "science",
    title: "Cleaning Chemicals",
    description:
      "Professional-grade detergents, degreasers, disinfectants, and floor care chemicals for all facility types. Available in concentrate or ready-to-use formats, from 5-litre refills to 25-litre drums.",
    bullets: [
      { icon: "eco", text: "Eco-friendly & biodegradable options" },
      { icon: "verified", text: "Full safety data sheets (SDS) provided" },
      { icon: "local_shipping", text: "Bulk formats for high-volume facilities" },
    ],
  },
  {
    icon: "sanitizer",
    title: "Hygiene & Disposables",
    description:
      "Hand soap, alcohol sanitiser, paper towels, toilet tissue, and commercial wipes supplied in case quantities. Sustainable FSC-certified options available for facilities with environmental commitments.",
    bullets: [
      { icon: "water_drop", text: "Paper towels, tissue & centrefeed rolls" },
      { icon: "inventory_2", text: "Bulk supply & auto-replenishment available" },
      { icon: "recycling", text: "Recycled & eco-certified options" },
    ],
    reverse: true,
  },
  {
    icon: "cleaning_services",
    title: "Janitorial Equipment",
    description:
      "Mops, buckets, trolleys, scrubbing machines, and hand tools from leading brands. Everything your cleaning team needs to operate efficiently across all facility sizes.",
    bullets: [
      { icon: "settings", text: "Manual & battery-powered floor machines" },
      { icon: "brush", text: "Brushes, squeegees & specialist tools" },
      { icon: "store", text: "Leading brands stocked" },
    ],
  },
  {
    icon: "back_hand",
    title: "PPE & Protective Clothing",
    description:
      "Gloves, masks, aprons, coveralls, and hi-vis wear for cleaning, maintenance, and facilities staff. All products meet EU Personal Protective Equipment Regulation (EU) 2016/425.",
    bullets: [
      { icon: "masks", text: "FFP2/FFP3 respirators & face shields" },
      { icon: "checkroom", text: "Disposable coveralls & waterproof aprons" },
      { icon: "gpp_good", text: "EU PPE Regulation compliant" },
    ],
    reverse: true,
  },
  {
    icon: "wash",
    title: "Washroom Supplies",
    description:
      "Dispensers, soap, air fresheners, sanitary units, and consumable refills for commercial washrooms. Keep facilities hygienic and well-presented for staff and visitors.",
    bullets: [
      { icon: "air", text: "Automatic air fresheners & odour control" },
      { icon: "wc", text: "Sanitary & clinical disposal units" },
      { icon: "star", text: "Visitor-ready presentation standard" },
    ],
  },
  {
    icon: "delete",
    title: "Bin Liners & Waste Management",
    description:
      "Heavy-duty bin liners, recycling bags, clinical waste bags, and waste segregation supplies for all facility types. Compliant with Irish waste management regulations.",
    bullets: [
      { icon: "recycling", text: "Colour-coded segregation bags & labels" },
      { icon: "medical_services", text: "Clinical & cytotoxic waste solutions" },
      { icon: "business", text: "Bulk orders for all facility sizes" },
    ],
    reverse: true,
  },
];

function BulletItem({ icon, text }) {
  return (
    <MKBox display="flex" alignItems="center" gap={1.5} mb={1.5}>
      <MKBox
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1,
          background: "#fff8ee",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 18, color: "#ff9f1c" }}>{icon}</Icon>
      </MKBox>
      <MKTypography variant="body2" sx={{ color: "#495057", fontWeight: 500 }}>
        {text}
      </MKTypography>
    </MKBox>
  );
}

BulletItem.propTypes = {
  icon: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
};

function IconBlock({ icon }) {
  return (
    <MKBox display="flex" justifyContent="center" alignItems="center">
      <MKBox
        sx={{
          width: { xs: 140, md: 180 },
          height: { xs: 140, md: 180 },
          borderRadius: "50%",
          background: "linear-gradient(135deg, #fff8ee 0%, #ffe8bb 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 24px 64px rgba(255,159,28,0.18)",
        }}
      >
        <Icon sx={{ fontSize: "72px !important", color: "#ff9f1c" }}>{icon}</Icon>
      </MKBox>
    </MKBox>
  );
}

IconBlock.propTypes = {
  icon: PropTypes.string.isRequired,
};

function Products() {
  return (
    <MKBox id="supplies-products">
      <SectionLayout
        title="Everything your facility needs"
        description="Professional-grade supplies for offices, schools, healthcare, hospitality, and more."
        contents={
          <MKBox mt={4}>
            {productRows.map(({ icon, title, description, bullets, reverse }, index) => {
              const iconCol = (
                <Grid item xs={12} md={5} key="icon">
                  <IconBlock icon={icon} />
                </Grid>
              );
              const textCol = (
                <Grid item xs={12} md={7} key="text">
                  <MKBox pl={{ xs: 0, md: reverse ? 0 : 4 }} pr={{ xs: 0, md: reverse ? 4 : 0 }}>
                    <MKTypography variant="h4" fontWeight="bold" sx={{ color: "#32373c", mb: 2 }}>
                      {title}
                    </MKTypography>
                    <MKTypography variant="body2" sx={{ color: "#6c757d", lineHeight: 1.8, mb: 3 }}>
                      {description}
                    </MKTypography>
                    {bullets.map((b) => (
                      <BulletItem key={b.text} icon={b.icon} text={b.text} />
                    ))}
                  </MKBox>
                </Grid>
              );

              return (
                <Container key={title}>
                  <Grid
                    container
                    spacing={6}
                    alignItems="center"
                    sx={{ mb: index < productRows.length - 1 ? { xs: 8, md: 12 } : 0 }}
                  >
                    {reverse ? [textCol, iconCol] : [iconCol, textCol]}
                  </Grid>
                </Container>
              );
            })}
          </MKBox>
        }
      />
    </MKBox>
  );
}

export default Products;
